// The Nomadic mark as inline SVG with two brand animations:
//
//   variant="loading"  — "Loading-Thinking trace-Reasoning": the four blades
//                        pulse outward twice, then the mark spins 360°. 2.3s.
//   variant="agent"    — "Logo Animation for Nomadic AI agent": a sparkle spins
//                        in, pops into the pinwheel, the blades pulse once, then
//                        it pops back to the sparkle — all under a 540° spin. 3.5s.
//
// Geometry (blade + sparkle paths, anchors, per-blade positions and rotations)
// and the timings are transcribed from those two Lottie files; the motion lives
// in `as-load-*` / `as-agent-*` in nomadic-mark.css.
//
// Inlined rather than played through a Lottie runtime: lottie-web is ~250 KB for
// what renders at 20-28 px, and CSS keyframes reproduce these two animations
// (translate / rotate / scale / opacity, no path morphing) exactly.
import type { CSSProperties } from "react";
import "../styles/nomadic-mark.css";

/** One "N" blade, in its own 71.4 × 63.1 space (polygon, no curves). */
const BLADE =
  "M54.8 51.2 L54.6 51.2 L29.5 0.1 L0.2 0 L0 63.1 L16.6 63.1 L16.6 16.5 L18.9 16.5 L41.9 63.1 L71.4 63.1 L71.4 43.1 L54.8 25.9 Z";
/** The four-point sparkle the agent animation morphs through, in 131 × 131. */
const SPARKLE =
  "M51.3 0 L51.4 0 L76.6 51.2 L131.1 51.3 L131.1 51.4 L80 76.6 L79.9 131.1 L79.7 131.1 L54.6 80 L0 79.9 L0 79.7 L51.1 54.6 Z";

const BLADE_ANCHOR = [35.685, 31.535]; // a blade's own centre — its rotation anchor
const PIN_CENTRE = [127.4, 127.4]; // the pinwheel's centre in the Lottie's canvas
const SPARKLE_CENTRE = [65.566, 65.558];
const GROUP_OFFSET = 50; // the Lottie group offset that centres the pinwheel

/** Rest position + rotation per blade, and the direction it pulses outward. */
const BLADES = [
  { x: 31.535, y: 72.575, r: 90, ox: "-24px", oy: "0px" },
  { x: 72.607, y: 123.242, r: 0, ox: "0px", oy: "24px" },
  { x: 123.285, y: 82.217, r: -90, ox: "24px", oy: "0px" },
  { x: 82.208, y: 31.535, r: -180, ox: "0px", oy: "-24px" },
];

// Everything is drawn about (0, 0) and the viewBox is centred there, so a CSS
// rotate/scale lands on the mark's middle whether the browser resolves the
// transform origin as SVG user space (0, 0) or as the view-box centre (50% 50%)
// — the two disagree for <g> elements, which is what made it spin off-corner.
//
// EXTENT is the tightest square that holds every frame: the mark's circumscribed
// radius is 83 at rest and 106 with the blades pushed out, and the agent variant
// spins while they're out, so 2 × 106.
const EXTENT = 212;
const VIEW_BOX = `${-EXTENT / 2} ${-EXTENT / 2} ${EXTENT} ${EXTENT}`;

export interface NomadicMarkProps {
  className?: string;
  /** Run the animation. At rest both variants render the plain pinwheel. */
  animate?: boolean;
  /** "loading" for progress/trace indicators, "agent" for the assistant's logo. */
  variant?: "loading" | "agent";
  /** The mark fills with `currentColor` — set `color` here to tint it. */
  style?: CSSProperties;
}

export function NomadicMark({
  className,
  animate = false,
  variant = "loading",
  style,
}: NomadicMarkProps) {
  const agent = variant === "agent";
  const on = animate;

  return (
    <svg
      viewBox={VIEW_BOX}
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      <g className={on ? (agent ? "as-agent-spin" : "as-load-spin") : undefined}>
        {/* Pinwheel. In the agent variant it scales in and back out again. */}
        <g className={on && agent ? "as-agent-pin" : undefined}>
          <g transform={`translate(${-PIN_CENTRE[0]} ${-PIN_CENTRE[1]})`}>
            {BLADES.map((b) => (
              <g
                key={b.r}
                className={
                  on
                    ? agent
                      ? "as-agent-blade"
                      : "as-load-blade"
                    : undefined
                }
                style={
                  on
                    ? ({ "--ox": b.ox, "--oy": b.oy } as CSSProperties)
                    : undefined
                }
              >
                <path
                  d={BLADE}
                  fill="currentColor"
                  transform={`translate(${b.x + GROUP_OFFSET} ${b.y + GROUP_OFFSET}) rotate(${b.r}) translate(${-BLADE_ANCHOR[0]} ${-BLADE_ANCHOR[1]})`}
                />
              </g>
            ))}
          </g>
        </g>

        {/* The sparkle only exists while the agent animation is running. */}
        {on && agent && (
          <g className="as-agent-sparkle">
            <path
              d={SPARKLE}
              fill="currentColor"
              transform={`translate(${-SPARKLE_CENTRE[0]} ${-SPARKLE_CENTRE[1]})`}
            />
          </g>
        )}
      </g>
    </svg>
  );
}
